import React, { useState, useEffect } from 'react';
import { Container, Form, Alert, Row, Col, Button } from 'react-bootstrap';
import { Auth } from 'aws-amplify';
import './ForgotPassword.css';
import LoaderButton from '../components/LoaderButton';
import PasswordField from '../components/PasswordField';
import PasswordRequirements from '../components/PasswordRequirements';
import { useAppContext } from '../libs/contextLib';
import { useFormFields } from '../libs/hooksLib';
import { UiError } from '../libs/errorLib';
import TimedRedirect from '../components/TimedRedirect';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { useNavigate } from 'react-router-dom';

export default function ForgotPassword() {
  const { setError } = useAppContext();
  // We initialise to true so we don't start validatring the form until the user
  // has clicked submit the first time. This prevents annoying messages from
  // showing until the user is 'done'.
  // Split the form up into two parts. The request code, and reset password bit.
  // We will call these top and bottom.
  const [topIsValid, setTopIsValid]           = useState(true);
  const [bottomIsValid, setBottomIsValid]     = useState(true);
  const [topIsLoading, setTopIsLoading]       = useState(false);
  const [bottomIsLoading, setBottomIsLoading] = useState(false);
  const [redirect, setRedirect]               = useState(false);
  const {user}                                = useAppContext();
  const { trackEvent }                        = useMatomo();
  const navigate                              = useNavigate();
  const [fields, handleFieldChange]           = useFormFields({
    email: user !== null && user.hasOwnProperty('username') ? user.username : '',
    code: '',
    password: '',
    password_repeat: ''
  });

  /**
   * Called after fields is updated in handleFieldChange.
   * Will validate form if state currently isn't valid.
   */
  useEffect(() => {
      if (!topIsValid) {
        validateTopForm();
      }
      if (!bottomIsValid) {
        validateBottomForm();
      }
    },
    // eslint-disable-next-line
    [topIsValid, bottomIsValid, fields, user]
  );

  function validateTopForm() {
    let valid = true;
    if (
      fields.email.length === 0
    ) {
      valid = false;
      setError(
        new UiError('forgot',  'Please ensure you have entered a valid email address.')
      );
    }

    // Potentially clear errors.
    if (valid) {
      setError(
        new UiError('forgot',  '', 'resolved')
      );
    }
    setTopIsValid(valid);
    return valid;
  }

  function validateBottomForm() {
    let valid = true;
    if (
      fields.code.length === 0 ||
      fields.password.length === 0 ||
      fields.password_repeat.length === 0
    ) {
      valid = false;
      setError(
        new UiError('forgot',  'Please ensure the confirmation code, new password and repeat new password fields are filled.')
      );
    }

    if (
      fields.password !== fields.password_repeat
    ) {
      valid = false;
      setError(
        new UiError('forgot',  'Please ensure your passwords match.')
      );
    }

    // Potentially clear errors.
    if (valid) {
      setError(
        new UiError('forgot',  '', 'resolved')
      );
    }
    setBottomIsValid(valid);
    return valid;
  }

  /**
   * Function to handle the user hitting "Send code".
   * Will send an email with confirmation code to supplied address.
   * @param {event} event 
   */
  async function handleTopSubmit(event) {
    event.preventDefault();
    if (!validateTopForm()) {
      return;
    }
    setTopIsLoading(true);
    trackEvent({
      category: 'identity',
      action: 'password reset',
      name: 'request',
    });
    Auth.forgotPassword(fields.email.trim()).then((result) => {
      // Success!
      setError({key: 'forgot', message: "A validation code was sent to the supplied email address if it is linked to an account in our systems.", severity: 'success'});
      setTopIsLoading(false);
    }, (error) => {
      // Failure.
      setError({key: 'forgot', message: "A validation code could not be sent to the supplied email address: " + error.message, severity: 'danger'});
      setTopIsLoading(false);
    });
  }

  /**
   * Function to handle the user hitting "Update password".
   * Will change the password using the supplied code.
   * @param {event} event 
   */
  async function handleBottomSubmit(event) {
    event.preventDefault();
    if (!validateBottomForm()) {
      return;
    }
    setBottomIsLoading(true);
    Auth.forgotPasswordSubmit(fields.email.trim(), fields.code.trim(), fields.password.trim()).then((result) => {
      // Success!
      setError({key: 'forgot', message: "Your password has been updated.", severity: 'success'});
      setBottomIsLoading(false);
      setRedirect(true);
    }, (error) => {
      // Failure.
      setError({key: 'forgot', message: "Your password could not be updated: " + error.message, severity: 'danger'});
      setBottomIsLoading(false);
    });
    trackEvent({
      category: 'identity',
      action: 'password reset',
      name: 'set',
    });
  }

  return (
    <Container fluid="sm">
      <div className="narrowform">
        <Row>
            <Col>
            <h1 className="h3 mb-2 mt-2 font-weight-normal">Password Reset.</h1> 
            <Form className="form-forgot-password pb-0" onSubmit={handleTopSubmit}>
              {
                redirect ? (
                  <TimedRedirect
                    className = "mb-2"
                  />
                ) : (
                  <Alert variant="primary" className="mb-2">
                    Send a code to the email address you used to register to reset your password.
                  </Alert>
                )
              }

              <div className="d-grid">
                <Form.Control 
                  autoFocus
                  id="email"
                  placeholder="Email address"
                  type="email"
                  autoComplete="username"
                  value={fields.email}
                  onChange={handleFieldChange}
                />
                <Form.Label htmlFor="email" className="form-label visually-hidden">Email</Form.Label>
                <LoaderButton
                  className="my-2"
                  block="true"
                  size="large"
                  type="submit"
                  id="topsubmit"
                  isLoading={topIsLoading}
                >
                  Send code.
                </LoaderButton>
              </div>
            </Form>
            <Form className="form-forgot-password" onSubmit={handleBottomSubmit}>
              <PasswordRequirements />
              <div className="d-grid">
                <div>
                  <Form.Control
                    id="code"
                    placeholder="Code"
                    type="text"
                    autoComplete="one-time-code"
                    value={fields.code}
                    onChange={handleFieldChange}
                  />
                  <Form.Label htmlFor="code" className="form-label visually-hidden">Validation code.</Form.Label>
                  <PasswordField
                    id="password"
                    placeholder="New password"
                    label="New password"
                    autoComplete="new-password"
                    value={fields.password}
                    onChange={handleFieldChange}
                    className="topinput"
                  />
                  <PasswordField
                    id="password_repeat"
                    placeholder="Confirm new password"
                    label="Confirm new password"
                    autoComplete="new-password"
                    value={fields.password_repeat}
                    onChange={handleFieldChange}
                    className="bottominput"
                  />
                </div>
                <LoaderButton className="mt-2"
                  block="true"
                  size="large"
                  type="submit"
                  id="bottomsubmit"
                  isLoading={bottomIsLoading}
                >
                  Update password
                </LoaderButton>
              </div>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button className="mt-2" variant="secondary" onClick={() => navigate.goBack()}>Back</Button>
          </Col>
        </Row>
      </div>
    </Container>
  );
}
